import type {
  CustomRoleTaxonomySelection,
  PostingExprNode,
  PostingNode,
} from '@revelio/data-access';

import { getRoleTaxonomyId as getRoleTaxonomyNumericId } from '../../../../../shared/selection-lists/utils/roleTaxonomy';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import {
  buildRoleValNode,
  buildTreeFilterNodes,
  type RefData,
  wrapExprNode,
} from './node-builders';

export const getRoleTaxonomyNumber = (filters: Filter[]): number => {
  const roleTaxonomyFilter = findFilterById(filters, FilterId.ROLE_TAXONOMY);
  const taxonomySelection =
    roleTaxonomyFilter?.type === FilterType.VALUE
      ? (roleTaxonomyFilter as SingleValueFilter<CustomRoleTaxonomySelection>)
          .value
      : undefined;

  return getRoleTaxonomyNumericId(taxonomySelection) ?? 0;
};

const ROLE_LEVEL_ID_TO_LEVEL: Record<string, number> = {
  role_k7: 1,
  role_k150: 3,
  role_k1500: 5,
  role_k15000: 7,
  role_k17000: 7,
};

const resolveRoleLevel = (levelId: string): number | null =>
  ROLE_LEVEL_ID_TO_LEVEL[levelId] ?? null;

const makeRoleLeafFactory =
  (taxonomyId: number) =>
  (id: number, levelId: string): PostingExprNode | null => {
    const level = resolveRoleLevel(levelId);
    if (level === null) return null;
    const ref: RefData = { level, taxonomyId };
    return buildRoleValNode(id, ref);
  };

export const buildRoleFilter = (filters: Filter[]): PostingNode | null => {
  const roleFilter = findFilterById(filters, FilterId.ROLE);
  if (!roleFilter || roleFilter.type !== FilterType.TREE) {
    return null;
  }

  const makeLeaf = makeRoleLeafFactory(getRoleTaxonomyNumber(filters));
  const exprNode = buildTreeFilterNodes(roleFilter, makeLeaf);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, roleFilter.isNot);
};

export const buildMultiRoleFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiRole = findFilterById(filters, FilterId.MULTI_ROLE);
  if (!multiRole || !isMultiFilter(multiRole)) {
    return null;
  }
  if (multiRole.instances.length === 0) {
    return null;
  }

  const makeLeaf = makeRoleLeafFactory(getRoleTaxonomyNumber(filters));

  const instanceNodes = multiRole.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(instance, makeLeaf);
      if (!exprNode) return null;
      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is PostingNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }
  return instanceNodes;
};
